import React, { useState, useContext, useEffect, lazy, useRef } from "react";
import styles from "./FundamentalFilmProduction.module.css";
import { FaShoppingCart, FaHeart } from "react-icons/fa";
import { CiClock2 } from "react-icons/ci";
import { FilmProductionContext } from "../../store/FilmProductionContext";
import auth from "../../Auth/Auth";
import { useNavigate, Link, useLocation } from "react-router-dom";
import FilmProductionInstructor from "../FilmProductionInstructor/FilmProductionInstructor";
import FilmProductionCourseContent from "../../Component/FilmProductionCourseContent/FilmProductionCourseContent";
import { LanguageContext } from "../../Component/LanguageContext/LanguageContext";
import { wwlMap } from "./wwlMap";
import { wwlMapAr } from "./wwlMapAr";
import axios from "axios";
import riyals from "./riyals.svg";
import { useCart } from '../../context/CartContext';

const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;
const FundamentalFilmProduction = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const contentRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
  });

  const location = useLocation(); // Get the full URL path
  const courseId = location.pathname.split("/").pop(); // Extract last part of the URL
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [error, setError] = useState(null);

  // Handle initial loading and data fetching
  useEffect(() => {
    console.log("Loading Status:", {
      coursesLoaded: courses.length > 0,
      selectedCourseLoaded: !!selectedCourse,
      documentReady: document.readyState,
      imagesLoaded,
    });

    const checkLoading = () => {
      if (
        courses.length > 0 &&
        selectedCourse &&
        document.readyState === "complete" &&
        imagesLoaded
      ) {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    };

    checkLoading();

    // Fallback to hide loader after 5 seconds
    const fallbackTimer = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, [courses, selectedCourse, imagesLoaded]);

  // Scroll to top on initial load and handle back button navigation
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Set up proper back button handling
    if ("scrollRestoration" in history) {
      // This ensures the browser doesn't try to restore scroll position on navigation
      history.scrollRestoration = "manual";
    }

    // Force the browser to actually navigate back when the back button is clicked
    const handleBackButton = (e) => {
      // This checks if it's a popstate event (back/forward navigation)
      if (window.location.href !== document.referrer) {
        // Force browser to the previous page in history
        if (document.referrer) {
          window.location.href = document.referrer;
        } else {
          // If there's no referrer, at least navigate away from current page
          window.history.back();
        }
      }
    };

    // Attach the handler directly to the window.onpopstate
    // This overrides any other handlers that might be capturing and preventing the default
    window.onpopstate = handleBackButton;

    return () => {
      // Clean up the handler when component unmounts
      window.onpopstate = null;
    };
  }, []);

  // Check if all images are loaded
  useEffect(() => {
    if (contentRef.current) {
      const images = contentRef.current.getElementsByTagName("img");
      let loadedImages = 0;
      const totalImages = images.length;

      const imageLoaded = () => {
        loadedImages++;

        if (loadedImages === totalImages) {
          setImagesLoaded(true);
        }
      };

      if (totalImages === 0) {
        setImagesLoaded(true);
        return;
      }

      Array.from(images).forEach((img) => {
        if (img.complete) {
          imageLoaded();
        } else {
          img.addEventListener("load", imageLoaded);
          img.addEventListener("error", imageLoaded);
        }
      });

      return () => {
        Array.from(images).forEach((img) => {
          img.removeEventListener("load", imageLoaded);
          img.removeEventListener("error", imageLoaded);
        });
      };
    }
  }, [courses, selectedCourse]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${marketingBackendUrl}/maflam/courses`);
        if (!response.ok) throw new Error("Failed to fetch courses");
        const data = await response.json();
        setCourses(data.courses);

        // Find the matching course based on CourseIdEnglish
        const thiscourse = data.courses.find((course) => course._id === courseId);

        if (thiscourse) {
          setSelectedCourse(thiscourse);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [courseId]); // Refetch when courseId changes

  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const userEmail = localStorage.getItem("useremailidis");
        if (!userDetails || !selectedCourse) return;

        const response = await axios.get(
          `${marketingBackendUrl}/maflam/wishlist/${userEmail}`
        );

        if (response.data.success) {
          const exists = response.data.data.some(
            (course) =>
              course.courseNameEnglish === selectedCourse.CourseNameEnglish
          );
          setIsInWishlist(exists);
        }
      } catch (error) {
        console.error("Error checking wishlist status:", error);
      }
    };

    if (selectedCourse) {
      checkWishlistStatus();
    }
  }, [selectedCourse]);

  const userDetails = auth.getAuthData();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const toggleContent = () => {
    setIsOpen(!isOpen);
  };

  const checkoutpage = (paymentId) => {
    if (selectedCourse.CourseCostEnglish === "0") {
      navigate("/home/checkout/679ca8ec781c87446493d5c7");
    } else if (userDetails) {
      navigate(`/home/checkout/${paymentId}`);
    } else {
      // navigate("/home/login");
      console.log("User Not logged in Here!!");
      navigate(`/home/checkout/${paymentId}`);
    }
  };

  // const checkoutpage = (paymentId) => {
  //   if (thiscourse.CourseCostEnglish === "0") {
  //     navigate("/home/checkout/679ca8ec781c87446493d5c7");
  //   } else if (auth.isAuthenticated()) {
  //     navigate(`/home/checkout/${paymentId}`);
  //   } else {
  //     // Instead of navigation, allow PaymentForm to handle guest purchase
  //     console.log("User is not logged in - allow local flow.");
  //   }
  // };

  const handleWishlistToggle = async () => {
    try {
      const userEmail = localStorage.getItem("useremailidis");

      if (!userEmail) {
        // Navigate to login page if user is not logged in
        navigate("/home/login");
        return;
      }

      if (isInWishlist) {
        // Remove from wishlist
        const response = await axios.post(
          `${marketingBackendUrl}/maflam/wishlist/remove-course`,
          {
            userEmail,
            courseNameEnglish: selectedCourse.CourseNameEnglish,
          }
        );

        if (response.data.success) {
          setIsInWishlist(false);
          setNotification({
            show: true,
            message:
              direction === "rtl"
                ? "تمت إزالة الدورة من قائمة الرغبات بنجاح"
                : "Course removed from wishlist successfully",
          });
          setTimeout(() => {
            setNotification({ show: false, message: "" });
          }, 2000);
        }
      } else {
        // Add to wishlist
        const courseDetails = {
          courseNameEnglish: selectedCourse.CourseNameEnglish,
          courseNameArabic: selectedCourse.CourseNameArabic,
          courseThumbnail: selectedCourse.ThumbnailEnglish,
          redirectionLink: `/home/R2/R4/filmproduction/${selectedCourse._id}`,
        };

        const response = await axios.post(
          `${marketingBackendUrl}/maflam/wishlist`,
          {
            userEmail,
            courseDetails,
          }
        );

        if (response.data.success) {
          setIsInWishlist(true);
          setNotification({
            show: true,
            message:
              direction === "rtl"
                ? "تمت إضافة الدورة إلى قائمة الرغبات بنجاح"
                : "Course added to wishlist successfully",
          });
          setTimeout(() => {
            setNotification({ show: false, message: "" });
          }, 2000);
        }
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setNotification({
          show: true,
          message:
            direction === "rtl"
              ? "الدورة موجودة بالفعل في قائمة الرغبات"
              : "Course already exists in wishlist",
        });
        setTimeout(() => {
          setNotification({ show: false, message: "" });
        }, 2000);
      } else {
        setNotification({
          show: true,
          message:
            direction === "rtl"
              ? "فشل في تحديث قائمة الرغبات"
              : "Failed to update wishlist",
        });
        setTimeout(() => {
          setNotification({ show: false, message: "" });
        }, 2000);
        console.error("Error updating wishlist:", error);
      }
    }
  };

  const handleVideoHover = (video, isHovering) => {
    if (isHovering) {
      video.play().catch((error) => {});
      setIsVideoPlaying(true);
    } else {
      video.pause();
      video.currentTime = 0; // Reset to start
      setIsVideoPlaying(false);
    }
  };

  const handleVideoLoad = (video) => {
    // Set current time to 5 seconds
    video.currentTime = 5;

    // Create a poster from the 5-second mark
    const createPoster = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(video, 0, 0, canvas.width, canvas.height);
      video.poster = canvas.toDataURL();
      // Reset video time to start
      video.currentTime = 0;
    };

    // Once we've seeked to 5 seconds, create the poster
    video.addEventListener("seeked", createPoster, { once: true });
  };

  // Helper to convert Arabic-Indic numerals to Western numerals
  function parseArabicNumber(str) {
    if (typeof str === 'string') {
      return Number(str.replace(/[\u0660-\u0669]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
    }
    return Number(str);
  }

  const addToCustomCart = async (course) => {
    // Bundle logic: Only add the bundle if any of the three editing courses are added
    const editingCourseIds = [
      '679914aaf6a6c8891dd9b4a2', // Adobe Premiere Pro
      '679914baf6a6c8891dd9b4b3', // Vegas Pro
      '679914c8f6a6c8891dd9b4c4'  // DaVinci Resolve
    ];
    const bundleCourseId = '679ca8ec781c87446493d5c7';

    if (editingCourseIds.includes(course._id)) {
      const bundleCourse = {
        _id: bundleCourseId,
        CourseNameArabic: 'حزمة أساسيات صناعة الأفلام',
        CourseNameEnglish: 'Filmmaking Fundamentals Package',
        priceInArab: 2200,
        priceInEng: 2200,
        CourseCostArabic: 2200,
        CourseCostEnglish: 2200,
        ThumbnailArabic: '',
        ThumbnailEnglish: '',
        CourseDescriptionArabic: 'تشمل جميع دورات المونتاج الأساسية.',
        CourseDescriptionEnglish: 'Includes all fundamental editing courses.'
      };
      setNotification({
        show: true,
        message: direction === "rtl"
          ? "فقط الباقة متاحة لدورات المونتاج. تم إضافة الباقة إلى السلة."
          : "Only the bundle can be added for editing courses. The bundle has been added to your cart."
      });
      course = bundleCourse;
    }

    try {
      await addToCart(course);
      setNotification({
        show: true,
        message: direction === "rtl"
          ? "تمت إضافة الدورة إلى سلة التسوق بنجاح"
          : "Course added to cart successfully"
      });
      setTimeout(() => {
        setNotification({ show: false, message: "" });
        navigate("/home/cart");
      }, 2000);
    } catch (error) {
      // Check if the error is "course already in cart"
      const errorMessage = error.message || "";
      const isAlreadyInCart = errorMessage.toLowerCase().includes("already in cart") || 
                             errorMessage.toLowerCase().includes("course already in cart") ||
                             errorMessage.toLowerCase().includes("course already in cart") ||
                             errorMessage.toLowerCase().includes("الدورة موجودة بالفعل");
      
      if (isAlreadyInCart) {
        setNotification({
          show: true,
          message: direction === "rtl"
            ? "الدورة موجودة بالفعل في سلة التسوق"
            : "Course Already in Cart"
        });
        setTimeout(() => {
          setNotification({ show: false, message: "" });
        }, 2000);
      } else {
        setNotification({
          show: true,
          message: direction === "rtl"
            ? (error.message || "فشل في إضافة الدورة إلى سلة التسوق")
            : (error.message || "Failed to add course to cart")
        });
        setTimeout(() => {
          setNotification({ show: false, message: "" });
        }, 2000);
      }
    }
  };

  // Add a helper function to remove from guest cart by productId
  const removeFromGuestCart = (productId) => {
    let guestCart = localStorage.getItem('guestCart');
    guestCart = guestCart ? JSON.parse(guestCart) : { items: [], totalAmount: 0 };
    guestCart.items = guestCart.items.filter(item => item.productId !== productId);
    guestCart.totalAmount = guestCart.items.reduce((sum, item) => sum + (item.price || 0), 0);
    localStorage.setItem('guestCart', JSON.stringify(guestCart));
    setNotification({
      show: true,
      message: direction === "rtl"
        ? "تمت إزالة الدورة من السلة"
        : "Course removed from cart"
    });
    setTimeout(() => {
      setNotification({ show: false, message: "" });
    }, 2000);
  };

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <span className={styles.loader}></span>
      </div>
    );
  }

  if (error) return <p>Error: {error}</p>;

  // Destructure required data from filmproductionScreenDetails
  const courseDataId = selectedCourse.CourseIdEnglish || {};
  const courseData = selectedCourse.CourseDescriptionEnglish || {};
  const buttonData = "None" || {};
  const { direction } = useContext(LanguageContext);
  const CourseId =
    direction == "rtl" ? selectedCourse.CourseIdArabic : selectedCourse.CourseIdEnglish;
  const CourseMoodleId =
    direction == "rtl"
      ? selectedCourse.CourseMoodleIdArabic
      : selectedCourse.CourseMoodleIdEnglish;
  const CourseName =
    direction == "rtl"
      ? selectedCourse.CourseNameArabic
      : selectedCourse.CourseNameEnglish;
  const CourseDescription =
    direction == "rtl"
      ? selectedCourse.CourseDescriptionArabic
      : selectedCourse.CourseDescriptionEnglish;
  const Thumbnail =
    direction == "rtl"
      ? selectedCourse.ThumbnailArabic
      : selectedCourse.ThumbnailEnglish;
  const CourseCategory =
    direction == "rtl"
      ? selectedCourse.CourseCategoryArabic
      : selectedCourse.CourseCategoryEnglish;
  const CourseLevel =
    direction == "rtl"
      ? selectedCourse.CourseLevelArabic
      : selectedCourse.CourseLevelEnglish;
  const EstimatedLearningTime =
    direction == "rtl"
      ? selectedCourse.EstimatedLearningTimeArabic
      : selectedCourse.EstimatedLearningTimeEnglish;
  const CourseCost =
    direction == "rtl"
      ? (selectedCourse.priceInArab || selectedCourse.CourseCostArabic || 0)
      : (selectedCourse.priceInEng || selectedCourse.CourseCostEnglish || 0);
  const LectureCount =
    direction == "rtl"
      ? selectedCourse.LectureCountArabic
      : selectedCourse.LectureCountEnglish;
  const CourseRanking =
    direction == "rtl"
      ? selectedCourse.CourseRankingArabic
      : selectedCourse.CourseRankingEnglish;
  const CourseCategoryType =
    direction == "rtl"
      ? selectedCourse.CourseCategoryTypeArabic
      : selectedCourse.CourseCategoryTypeEnglish;
  const CourseInstructorName =
    direction == "rtl"
      ? selectedCourse.CourseInstructorNameArabic
      : selectedCourse.CourseInstructorNameEnglish;
  const CourseWhatYouLearn =
    direction == "rtl"
      ? selectedCourse.CourseWhatyouLearnArabic
      : selectedCourse.CourseWhatyouLearnEnglish;
  const CourseInstructorDetails =
    direction == "rtl"
      ? selectedCourse.CourseInstructorDetailsArabic
      : selectedCourse.CourseInstructorDetailsEnglish;
  const CourseInstructorPhoto =
    direction == "rtl"
      ? selectedCourse.CourseInstructorPhotoArabic
      : selectedCourse.CourseInstructorPhotoEnglish;
  const CourseContent =
    direction == "rtl"
      ? selectedCourse.CourseContentArabic
      : selectedCourse.CourseContentEnglish;

  return (
    <div ref={contentRef}>
      {notification.show && (
        <div className={styles.notification}>{notification.message}</div>
      )}
      <div className={styles.coursedetailsheader}>
        <div className={styles.videoTheory}>
          <div className={styles.headerText}>
            <h6>{CourseName || "Fundamentals of Film Production"}</h6>
            <p> {CourseDescription || "Course description not available."}</p>
            <div className={styles.lessonsprice}>
              <div className={styles.time}>
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                >
                  <path
                    d="M10 0.5C8.02219 0.5 6.08879 1.08649 4.4443 2.1853C2.79981 3.28412 1.51809 4.8459 0.761209 6.67316C0.00433284 8.50043 -0.1937 10.5111 0.192152 12.4509C0.578004 14.3907 1.53041 16.1725 2.92894 17.5711C4.32746 18.9696 6.10929 19.922 8.0491 20.3078C9.98891 20.6937 11.9996 20.4957 13.8268 19.7388C15.6541 18.9819 17.2159 17.7002 18.3147 16.0557C19.4135 14.4112 20 12.4778 20 10.5C20 9.18678 19.7413 7.88642 19.2388 6.67316C18.7362 5.45991 17.9997 4.35752 17.0711 3.42893C16.1425 2.50035 15.0401 1.76375 13.8268 1.2612C12.6136 0.758658 11.3132 0.5 10 0.5ZM10 18.5C8.41775 18.5 6.87103 18.0308 5.55544 17.1518C4.23985 16.2727 3.21447 15.0233 2.60897 13.5615C2.00347 12.0997 1.84504 10.4911 2.15372 8.93928C2.4624 7.38743 3.22433 5.96197 4.34315 4.84314C5.46197 3.72433 6.88743 2.9624 8.43928 2.65372C9.99113 2.34504 11.5997 2.50346 13.0615 3.10896C14.5233 3.71446 15.7727 4.73984 16.6518 6.05544C17.5308 7.37103 18 8.91775 18 10.5C18 12.6217 17.1571 14.6566 15.6569 16.1569C14.1566 17.6571 12.1217 18.5 10 18.5Z"
                    fill="#39FFFB"
                  />
                  <path
                    d="M14 9.5H11V6.5C11 6.23478 10.8946 5.98043 10.7071 5.79289C10.5196 5.60536 10.2652 5.5 10 5.5C9.73478 5.5 9.48043 5.60536 9.29289 5.79289C9.10536 5.98043 9 6.23478 9 6.5V10.5C9 10.7652 9.10536 11.0196 9.29289 11.2071C9.48043 11.3946 9.73478 11.5 10 11.5H14C14.2652 11.5 14.5196 11.3946 14.7071 11.2071C14.8946 11.0196 15 10.7652 15 10.5C15 10.2348 14.8946 9.98043 14.7071 9.79289C14.5196 9.60536 14.2652 9.5 14 9.5Z"
                    fill="#39FFFB"
                  />
                </svg>
                {EstimatedLearningTime}
              </div>
              <div className={styles.lessons}>
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                >
                  <path
                    d="M10.5 20C9.21772 20 7.97365 19.7488 6.80214 19.2533C5.67078 18.7748 4.65492 18.09 3.78268 17.2175C2.91043 16.3451 2.22539 15.3292 1.74687 14.1981C1.25118 13.0264 1 11.7823 1 10.5C1 9.21772 1.25118 7.97365 1.74666 6.80214C2.22518 5.67078 2.91 4.65492 3.78246 3.78268C4.65492 2.91043 5.67078 2.22539 6.80193 1.74687C7.97344 1.25118 9.21772 1 10.5 1C11.7823 1 13.0264 1.25118 14.1979 1.74666C15.3292 2.22518 16.3451 2.91 17.2173 3.78246C18.0896 4.65492 18.7746 5.67078 19.2531 6.80193C19.7486 7.97344 19.9998 9.21772 19.9998 10.4998C19.9998 11.7819 19.7486 13.0261 19.2531 14.1979C18.7746 15.3292 18.0898 16.3451 17.2173 17.2173C16.3449 18.0896 15.329 18.7746 14.1979 19.2531C13.0264 19.7486 11.7821 20 10.5 20ZM10.5 1.97314C9.34867 1.97314 8.23213 2.19856 7.18105 2.64294C6.16583 3.0723 5.25388 3.68736 4.47051 4.47051C3.68714 5.25366 3.0723 6.16562 2.64294 7.18105C2.19834 8.23191 1.97314 9.34867 1.97314 10.5C1.97314 11.6513 2.19856 12.7679 2.64294 13.8189C3.0723 14.8342 3.68736 15.7461 4.47051 16.5295C5.25366 17.3126 6.16562 17.9277 7.18105 18.3571C8.23191 18.8017 9.34867 19.0269 10.5 19.0269C11.6513 19.0269 12.7679 18.8014 13.8189 18.3571C14.8342 17.9277 15.7461 17.3126 16.5295 16.5295C17.3129 15.7463 17.9277 14.8344 18.3571 13.8189C18.8017 12.7681 19.0269 11.6513 19.0269 10.5C19.0269 9.34867 18.8014 8.23213 18.3571 7.18105C17.9277 6.16583 17.3126 5.25388 16.5295 4.47051C15.7463 3.68736 14.8344 3.0723 13.8189 2.64294C12.7681 2.19834 11.6513 1.97314 10.5 1.97314Z"
                    fill="#5AFFFF"
                    stroke="#5AFFFF"
                  />
                  <path
                    d="M9.22714 14.9998C9.03534 14.9998 8.84278 14.9544 8.66497 14.8621C8.25491 14.6495 8 14.2289 8 13.7647V7.23521C8 6.77094 8.25472 6.35041 8.66497 6.13777C9.07502 5.92513 9.56311 5.9607 9.93878 6.23021L14.4858 9.49494C14.8078 9.72603 15 10.1019 15 10.4999C15 10.898 14.8078 11.2737 14.4858 11.5049L9.93878 14.7697C9.72619 14.9224 9.47732 15 9.22714 15V14.9998ZM9.23016 6.86109C9.15287 6.86109 9.09014 6.88696 9.05707 6.90407C8.99679 6.93527 8.85658 7.03017 8.85658 7.23502V13.7645C8.85658 13.9693 8.99679 14.0642 9.05707 14.0954C9.11735 14.1266 9.27551 14.1865 9.44123 14.0675L13.9883 10.8027C14.0869 10.732 14.1434 10.6215 14.1434 10.4996C14.1434 10.3776 14.0869 10.2673 13.9883 10.1964L9.44123 6.93165C9.36659 6.87802 9.29346 6.86071 9.23016 6.86071V6.86109Z"
                    fill="#5AFFFF"
                    stroke="#5AFFFF"
                    stroke-width="0.1"
                  />
                </svg>

                {direction == "ltr"
                  ? `${LectureCount} Lessons `
                  : `${LectureCount} دروس`}
              </div>

              <div className={styles.riyals}>
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="23"
                  height="16"
                  viewBox="0 0 23 16"
                  fill="none"
                >
                  <g clip-path="url(#clip0_9474_5649)">
                    <path
                      d="M0 6.10547V16.1476H23V6.10547H0ZM22.0994 14.3813C21.6035 14.3813 21.1988 14.779 21.1988 15.2662C21.1988 15.277 21.1988 15.2913 21.1988 15.3021H1.79756C1.79756 15.3021 1.79756 15.2806 1.79756 15.2662C1.79756 14.779 1.39648 14.3813 0.896956 14.3813C0.886018 14.3813 0.875079 14.3813 0.860495 14.3813V7.86812C0.860495 7.86812 0.886018 7.86812 0.896956 7.86812C1.39283 7.86812 1.79756 7.47403 1.79756 6.98321C1.79756 6.97246 1.79756 6.95813 1.79756 6.94738H21.2061C21.2061 6.94738 21.2061 6.96888 21.2061 6.98321C21.2061 7.47045 21.6072 7.86812 22.1067 7.86812C22.1176 7.86812 22.1322 7.86812 22.1432 7.86812V14.3813C22.1432 14.3813 22.1213 14.3813 22.1067 14.3813H22.0994ZM13.9648 11.1283C13.9648 12.9841 12.86 14.4888 11.5 14.4888C10.14 14.4888 9.03519 12.9841 9.03519 11.1283C9.03519 9.27251 10.14 7.76781 11.5 7.76781C12.86 7.76781 13.9648 9.27251 13.9648 11.1283Z"
                      fill="#5AFFFF"
                    />
                    <path
                      d="M10.5588 4.39202C10.1322 4.50309 9.77125 4.75745 9.49414 5.10497H12.7939C12.1376 4.48159 11.3245 4.1914 10.5588 4.39202Z"
                      fill="#5AFFFF"
                    />
                    <path
                      d="M5.21 5.10524L19.7545 1.27541C19.7545 1.27541 19.7545 1.29691 19.7618 1.31124C19.8895 1.78415 20.3853 2.06359 20.863 1.9382C20.8739 1.9382 20.8849 1.92745 20.8958 1.92387L21.7636 5.10524H22.6569L21.2641 0L1.87012 5.10524H5.20636H5.21Z"
                      fill="#5AFFFF"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_9474_5649">
                      <rect width="23" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>

                {direction === "rtl" ? (
                  CourseCost === "0" ? (
                    "متاح فقط ضمن الباقة الكاملة"
                  ) : (
                    <>
                      {CourseCost}{" "}
                      <img
                        src={riyals}
                        alt="ريال"
                        className={styles.riyalsIcon}
                      />
                    </>
                  )
                ) : CourseCost === "0" ? (
                  "Only Available with Bundle"
                ) : (
                  <>
                    {CourseCost}{" "}
                    <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                  </>
                )}
              </div>
            </div>
            <div className={styles.maincontainer}>
              <div className={styles.collapsible}>
                <div className={styles.header}>
                  <div
                    onClick={toggleContent}
                    className={styles.toggleButton}
                    style={{ cursor: "pointer", color: "#39FFFb" }}
                  >
                    {isOpen ? "-" : "+"}
                  </div>
                  <h5 className={styles.toggle}>
                    {direction === "rtl"
                      ? "ماذا ستتعلم"
                      : "What you will learn"}
                  </h5>
                  <hr style={{ opacity: "0.4" }} className={styles.line} />
                </div>
                {isOpen && (
                  <div>
                    <p className={styles.answer}>
                      {direction == "rtl"
                        ? CourseWhatYouLearn
                        : CourseWhatYouLearn}
                    </p>
                  </div>
                )}
              </div>

              {CourseInstructorName !== "na" && (
                <FilmProductionInstructor
                  CourseInstructorName={CourseInstructorName}
                  CourseInstructorDetails={CourseInstructorDetails}
                  CourseInstructorPhoto={CourseInstructorPhoto}
                />
              )}
              <FilmProductionCourseContent CourseContent={CourseContent} />
            </div>
          </div>
          <div className={styles.courseVideo}>
            <div className={styles.videofixed}>
              {Thumbnail?.match(/\.(mp4|webm|ogg)$/i) ? (
                <video
                  style={{ width: "100%", objectFit: "cover" }}
                  controls
                  muted
                  loop
                  playsInline
                  defaultMuted
                  preload="auto"
                  className={styles.courseVideoPlayer}
                  onLoadedData={(e) => handleVideoLoad(e.target)}
                  onMouseEnter={(e) => handleVideoHover(e.target, true)}
                  onMouseLeave={(e) => handleVideoHover(e.target, false)}
                >
                  <source
                    src={Thumbnail}
                    type={`video/${Thumbnail.split(".").pop()}`}
                  />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img src={Thumbnail} alt="Course Preview" />
              )}
            </div>

            <div className={styles.videocontent}>
              <p>
                {direction == "rtl"
                  ? "نبذة عن الدورة"
                  : "An Overview of the Course"}
              </p>

              {CourseCost == "0" ? (
                <div className={styles.buttonContainer}>
                  <button
                    className={styles.subcribebtn}
                    style={{
                      backgroundColor: "rgb(57,255,251)",
                      color: "black",
                      border: "none",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      width: "100%",
                      textAlign: "center",
                      transition: "none",
                      marginBottom: "10px"
                    }}
                  >
                    <Link
                      style={{
                        textDecoration: "none",
                        display: "block",
                        width: "100%",
                        color: "black",
                      }}
                      to={"/home/checkout/679ca8ec781c87446493d5c7"}
                    >
                      <span style={{ width: "100%" }}>
                        {CourseCost == "0"
                          ? direction == "rtl"
                            ? "متاح فقط ضمن الباقة الكاملة"
                            : "Only Available With Bundle"
                          : buttonData?.title}
                      </span>
                    </Link>
                  </button>

                  {/* Custom Cart Button */}
                  <button
                    className={styles.subcribebtn}
                    onClick={() => addToCustomCart(selectedCourse)}
                    style={{
                      backgroundColor: "rgb(57,255,251)",
                      color: "black",
                      border: "none",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      transition: "none",
                      marginBottom: "15px"
                    }}
                  >
                    <FaShoppingCart style={{ height: "24px", width: "24px" }} />
                    &nbsp;&nbsp;
                    <span>
                      {direction === "rtl" ? "إضافة إلى سلة التسوق" : "Add to Cart"}
                    </span>
                  </button>

                  {/* Wishlist Button */}
                  <button
                    className={`${styles.addtowishlist} ${
                      isInWishlist ? styles.inWishlist : ""
                    }`}
                    onClick={handleWishlistToggle}
                    style={{
                      backgroundColor: "transparent",
                      color: "rgb(57,255,251)",
                      border: "1px solid rgb(57,255,251)",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      transition: "all 0.3s ease",
                      borderRadius: "25px",
                      gap: "8px"
                    }}
                  >
                    <FaHeart
                      style={{
                        height: "24px",
                        width: "24px",
                        fill: isInWishlist ? "rgb(57, 255, 251)" : "currentColor",
                      }}
                    />
                    <span>
                      {direction === "rtl"
                        ? isInWishlist
                          ? "إزالة من قائمة الرغبات"
                          : "إضافة إلى قائمة الرغبات"
                        : isInWishlist
                        ? "Remove from Wishlist"
                        : "Add to Wishlist"}
                    </span>
                  </button>
                </div>
              ) : (
                <div className={styles.buttonContainer}>
                  {/* Buy Now Button */}
                  <button
                    className={styles.subcribebtn}
                    onClick={() => checkoutpage(selectedCourse._id)}
                    style={{
                      backgroundColor: "rgb(57,255,251)",
                      color: "black",
                      border: "none",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      transition: "none",
                      marginBottom: "10px"
                    }}
                  >
                    <span style={{ width: '100%' }}>
                      {selectedCourse.CourseIdEnglish === "0"
                        ? "Only Available With Bundle"
                        : (direction === "rtl" ? "اشترك الآن" : "Buy Now")}
                    </span>
                  </button>

                  {/* Custom Cart Button */}
                  <button
                    className={styles.subcribebtn}
                    onClick={() => addToCustomCart(selectedCourse)}
                    style={{
                      backgroundColor: "rgb(57,255,251)",
                      color: "black",
                      border: "none",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      transition: "none",
                      marginBottom: "15px"
                    }}
                  >
                    <FaShoppingCart style={{ height: "24px", width: "24px" }} />
                    &nbsp;&nbsp;
                    <span>
                      {direction === "rtl" ? "إضافة إلى سلة التسوق" : "Add to Cart"}
                    </span>
                  </button>

                  {/* Wishlist Button */}
                  <button
                    className={`${styles.addtowishlist} ${
                      isInWishlist ? styles.inWishlist : ""
                    }`}
                    onClick={handleWishlistToggle}
                    style={{
                      backgroundColor: "transparent",
                      color: "rgb(57,255,251)",
                      border: "1px solid rgb(57,255,251)",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      transition: "all 0.3s ease",
                      borderRadius: "25px",
                      gap: "8px"
                    }}
                  >
                    <FaHeart
                      style={{
                        height: "24px",
                        width: "24px",
                        fill: isInWishlist ? "rgb(57, 255, 251)" : "currentColor",
                      }}
                    />
                    <span>
                      {direction === "rtl"
                        ? isInWishlist
                          ? "إزالة من قائمة الرغبات"
                          : "إضافة إلى قائمة الرغبات"
                        : isInWishlist
                        ? "Remove from Wishlist"
                        : "Add to Wishlist"}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundamentalFilmProduction;
